import * as React from 'react';
import { observer } from 'mobx-react-lite';
import { IButtonProps, IconButton } from '@fluentui/react/lib/Button';
import { getOptimalTextColorString } from 'owa-color-utils/lib/colorUtils';
import type { ColorScheme } from 'owa-color-utils/lib/ColorScheme';

let styles = require('./CalendarBarButtons.scss');
let classNames = require('classnames/bind').bind(styles);

export interface CalendarBarButtonsProps extends React.HTMLProps<HTMLDivElement> {
    buttonProps: IButtonProps[];
    colorScheme: ColorScheme;
    className?: string;
}

const CalendarBarButtons = observer(function CalendarBarButtons(props: CalendarBarButtonsProps) {
    const { buttonProps, className, colorScheme } = props;

    if (buttonProps.length <= 0) {
        return null;
    }

    return (
        <div className={classNames(styles.formButtons, className)}>
            {buttonProps.map(button => (
                <IconButton
                    {...button}
                    key={button.iconProps!.iconName}
                    className={classNames(styles.calendarBarIcon, button.className)}
                    styles={getButtonStyles(colorScheme)}
                />
            ))}
        </div>
    );
});

function getButtonStyles(colorScheme: ColorScheme) {
    const {
        rootColor,
        pressedColor,
        rootHoveredBgColor,
        rootExpandedBgColor,
        rootPressedBgColor,
    } = getColorSchemeFromProps(colorScheme);

    const style = {
        root: {
            color: rootColor,
            borderRadius: 2,
            height: 32,
            width: 32,
        },

        rootHovered: {
            color: pressedColor,
            backgroundColor: rootHoveredBgColor,
        },

        rootPressed: {
            color: pressedColor,
            backgroundColor: rootPressedBgColor,
        },

        rootExpanded: {
            color: pressedColor,
            backgroundColor: rootExpandedBgColor,
        },

        rootExpandedHovered: {
            color: pressedColor,
            backgroundColor: rootExpandedBgColor,
        },

        icon: {
            color: rootColor,
        },

        iconHovered: {
            color: pressedColor,
        },

        iconPressed: {
            color: pressedColor,
        },

        iconExpanded: {
            color: pressedColor,
        },

        iconExpandedHovered: {
            color: pressedColor,
        },

        menuIcon: {
            color: rootColor,
        },

        menuIconHovered: {
            color: pressedColor,
        },

        menuIconPressed: {
            color: pressedColor,
        },

        menuIconExpanded: {
            color: pressedColor,
        },

        menuIconExpandedHovered: {
            color: pressedColor,
        },
    };

    return style;
}

function getColorSchemeFromProps(colorScheme: ColorScheme) {
    return {
        rootColor: getOptimalTextColorString(
            colorScheme.calLighterAlt,
            colorScheme,
            colorScheme.calDark
        ),
        pressedColor: getOptimalTextColorString(
            colorScheme.calLight,
            colorScheme,
            colorScheme.calDarker
        ),
        rootPressedBgColor: colorScheme.calLight,
        rootExpandedBgColor: colorScheme.calLighter,
        rootHoveredBgColor: colorScheme.calLighter,
    };
}

export default CalendarBarButtons;
